import type {
  LibraryBookSchema,
  LibrarySchema,
  MediaSchema,
} from '~/types/schema';
import { LibraryTypes } from '~/types/adamTypes';
import type { Links, Link } from '~/types/ui';
import { Eva2Error, ErrorTypes } from '~/lib/errors/Eva2Error';

export type UseLibrary = {
  links: ComputedRef<Links>;
  library: Ref<LibrarySchema>;
  libraryBook: Ref<LibraryBookSchema>;
  media: Ref<Map<string, MediaSchema>>;
  load: (slugs: string[], depth: number) => Promise<boolean>;
};

export const INJECT_LIBRARY = Symbol(
  'useLibrary composable'
) as InjectionKey<UseLibrary>;

export const useLibrary = (type: LibraryTypes): UseLibrary => {
  const { library, libraryBook, media, fetch } = useLibraryPopulate();

  const route = useRoute();
  const profile = useProfileStore();

  const _getLinks = (includeSelfLib: boolean = true) => {
    if (!library.value && !libraryBook.value) return [];
    const parents = (library.value || libraryBook.value).parents;
    let routePath = '';
    const parentLinks = parents.map((parent, index) => {
      routePath = index === 0 ? '/nomad' : `${routePath}/${parent.slug}`;
      return {
        id: parent.id,
        slug: parent.slug,
        label: parent.title,
        url: routePath,
        hidden: false,
      };
    });
    if (includeSelfLib) {
      let currentLink;
      if (library.value) {
        currentLink = {
          url: route.fullPath,
          hidden: false,
          id: library.value.id,
          slug: library.value.slug,
          label: library.value.title,
        } as Link;
      } else {
        currentLink = {
          url: route.fullPath,
          hidden: false,
          id: libraryBook.value.id,
          slug: libraryBook.value.slug,
          label: libraryBook.value.metaTitle,
        } as Link;
      }
      parentLinks.push(currentLink);
    }

    return parentLinks;
  };

  const load = async (slugs, depth = 3) => {
    let params;
    let parents = [];
    if (slugs.length === 0 || slugs[0] === '') {
      params = {
        type: profile.needToDefineMainContent
          ? LibraryTypes.FreeLibraryRootBox
          : LibraryTypes.RootBox,
      };
      parents = [];
    } else {
      params = {
        slug: slugs.pop(),
      };
      parents = [
        { type: LibraryTypes.RootBox },
        ...slugs.map((slug) => {
          return { slug };
        }),
      ];
    }

    const result = await fetch(params, depth, parents);

    if (!result) {
      throw new Eva2Error(
        ErrorTypes.ContentNotFound,
        slugs.value,
        `missing lb with ${slugs.value.join(',')}`
      );
    }
    return result;
  };

  const links = computed(() => {
    return _getLinks(true);
  });

  return {
    links,
    library,
    libraryBook,
    media,
    load,
  };
};
